import { i18nDisabledHelp as i18n } from './i18n.ts'

/**
 * Helper class for disabled button tooltips.
 * @example
 * <VButton :disabled="buttonDisabled.disabled" :help="buttonDisabled.help">
 * computed() {
 *   buttonDisabled() {
 *     return new DisabledHelp('Reason 1', this.condition)
 *   }
 * }
 * @example
 * <VButton :disabled="buttonDisabled.disabled" :help="buttonDisabled.help">
 * computed() {
 *   buttonDisabled() {
 *     const disabledHelp = new DisabledHelp()
 *     disabledHelp.add('Reason 1', this.condition1)
 *     disabledHelp.add('Reason 2', this.condition2)
 *     return disabledHelp
 *   }
 * }
 */
export class DisabledHelp {
  messages: string[] = []
  thisButtonIsDisabledBecause = ''

  /**
   * Create one or more messages if the condition is true.
   */
  constructor(messages: string | string[] | Record<string, any> = [], condition = true, thisButtonIsDisabledBecause?: string) {
    this.thisButtonIsDisabledBecause = thisButtonIsDisabledBecause ?? i18n.thisButtonIsDisabledBecause
    const messagesType = Array.isArray('messages') ? 'array' : typeof messages

    if (!condition) {
      return this
    }

    if (typeof messages === 'string') {
      messages = [messages]
    } else if (messagesType === 'object') {
      messages = Object.keys(messages).filter((message) => (messages as Record<string, any>)[message])
    }

    if (Array.isArray(messages)) {
      for (const message of messages) {
        this.add(message)
      }
    }
  }

  /**
   * Add a message if the condition is true.
   */
  add(message: string, condition = true) {
    if (condition) {
      this.messages.push(`• ${message}`)
    }

    return this
  }

  /**
   * Create messages for standardised form errors.
   */
  addFormErrors(errors: Record<string, string[]>, checks: Record<string, string>) {
    for (const [key, message] of Object.entries(checks)) {
      this.add(message, !!errors[key] && delete errors[key])
    }
    this.add(i18n.invalidInput, !!Object.keys(errors).length)

    return this
  }

  /**
   * Get the help tooltip text.
   */
  get help(): string[] | string {
    if (this.messages.length === 0) {
      return ''
    }

    return [this.thisButtonIsDisabledBecause, ...this.messages]
  }

  /**
   * Whether the component should be disabled.
   */
  get disabled(): boolean {
    return this.messages.length > 0
  }
}
